import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ClipboardCheck, Calendar, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import api, { formatApiError, startModuleCheckout } from "../lib/api";
import { MODULES } from "../data/modules";

const field = "w-full bg-white border border-linen rounded-2xl px-5 py-4 text-sm text-ink placeholder:text-smoke/60 focus:outline-none focus:ring-2 focus:ring-sage/50 transition-shadow duration-300";

export default function EspaceClient() {
  const { user, logout } = useAuth();
  const { t, moduleText } = useI18n();
  const navigate = useNavigate();
  const [sub, setSub] = useState(undefined);
  const [dossier, setDossier] = useState({ poste: "", entreprise: "", telephone: "", objectifs: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selDate, setSelDate] = useState(null);
  const [selTime, setSelTime] = useState(null);
  const [topic, setTopic] = useState("");
  const [bilan, setBilan] = useState(undefined);

  useEffect(() => {
    if (user === false) navigate("/connexion");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/subscriptions/me").then(({ data }) => setSub(data.subscription)).catch(() => setSub(null));
    api.get("/dossier").then(({ data }) => setDossier(data.dossier)).catch(() => {});
    refreshCoaching();
    api.get("/tests/me").then(({ data }) => setBilan(data.result)).catch(() => setBilan(null));
  }, [user]);

  const refreshCoaching = () => {
    api.get("/coaching/slots").then(({ data }) => setSlots(data.days)).catch(() => {});
    api.get("/coaching/bookings/me").then(({ data }) => setBookings(data)).catch(() => {});
  };

  if (!user) {
    return <div data-testid="espace-loading" className="min-h-screen bg-paper flex items-center justify-center"><p className="font-serif italic text-xl text-smoke">{t("espace.loading")}</p></div>;
  }

  const dims = t("test.dims");

  const chooseModule = async (id) => {
    try {
      if (MODULES[id].price > 0) {
        await startModuleCheckout(id);
        return;
      }
      const { data } = await api.post("/subscriptions", { module_id: id });
      setSub({ module: data.module, status: "active", since: new Date().toISOString() });
      toast.success(`${t("sim.activate")} ${data.module.name}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveDossier = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/dossier", dossier);
      toast.success(t("espace.save"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const bookCoaching = async (e) => {
    e.preventDefault();
    if (!selDate || !selTime) return;
    try {
      await api.post("/coaching/bookings", { date: selDate, time: selTime, topic });
      toast.success(t("espace.book"));
      setTopic("");
      setSelDate(null);
      setSelTime(null);
      refreshCoaching();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelBooking = async (id) => {
    try {
      await api.delete(`/coaching/bookings/${id}`);
      refreshCoaching();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const formatDay = (iso) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="espace-client-page" className="min-h-screen bg-paper">
      <header className="border-b border-linen bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" aria-label="Accueil VAV"><Logo /></Link>
          <div className="flex items-center gap-4">
            <Link data-testid="espace-site-link" to="/" className="hidden md:inline text-sm text-smoke hover:text-ink transition-colors duration-300">{t("espace.site")}</Link>
            <button data-testid="espace-logout-button" onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-smoke hover:text-ink transition-colors duration-300">
              <LogOut size={16} strokeWidth={1.5} /> {t("espace.logout")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-xs uppercase tracking-[0.28em] text-sage">{t("nav.myspace")}</p>
          <h1 className="mt-4 font-serif text-4xl md:text-6xl tracking-tight text-ink">{t("espace.hello")} {user.name?.split(" ")[0]},</h1>
          <p className="mt-3 text-smoke text-sm md:text-base">{t("espace.sub")}</p>
          <Link data-testid="espace-offres-link" to="/offres" className="mt-5 inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink text-sm px-6 py-3 hover:border-ink hover:bg-white transition-colors duration-300">
            {t("espace.offresLink")}
          </Link>
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-5 gap-8">
          <motion.section
            data-testid="espace-module-card"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-2 rounded-3xl bg-ink text-paper p-8 md:p-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-paper/50">{t("espace.myModule")}</p>
            {sub === undefined ? (
              <p className="mt-6 text-paper/60 text-sm">{t("espace.loadingModule")}</p>
            ) : sub ? (
              <>
                <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-tight">{sub.module.name}</h2>
                <p className="mt-2 text-xs uppercase tracking-[0.18em] text-paper/50">{moduleText(sub.module.id, "range", sub.module.range)}</p>
                <p className="mt-5 text-sm text-paper/70 leading-relaxed">{moduleText(sub.module.id, "tagline", sub.module.tagline)}</p>
                <p className="mt-8 font-serif text-2xl">{sub.module.price_label}{sub.module.price > 0 && <span className="text-sm text-paper/60"> {t("sim.perMonth")}</span>}</p>
                <p className="mt-6 text-xs text-paper/40">{t("espace.activeNote")}</p>
                {sub.stripe_managed && (
                  <button
                    data-testid="manage-subscription-button"
                    onClick={openPortal}
                    className="mt-6 inline-flex rounded-full border border-white/25 text-paper text-xs px-5 py-2.5 hover:bg-white/10 transition-colors duration-300"
                  >
                    {t("espace.manage")}
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="mt-5 text-sm text-paper/70 leading-relaxed">{t("espace.noModule")}</p>
                <Link data-testid="espace-simulateur-cta" to="/simulateur" className="mt-6 inline-flex rounded-full bg-paper text-ink px-6 py-3 text-sm hover:bg-bone transition-colors duration-300">
                  {t("espace.ctaTest")}
                </Link>
                <div className="mt-8 grid grid-cols-2 gap-2">
                  {MODULES.map((m) => (
                    <button key={m.id} data-testid={`espace-module-choice-${m.id}`} onClick={() => chooseModule(m.id)} className="rounded-xl border border-white/15 px-3 py-2.5 text-left hover:border-sage transition-colors duration-300">
                      <span className="block text-sm">{m.name}</span>
                      <span className="block text-xs text-paper/50 mt-0.5">{m.priceLabel}{m.price > 0 ? ` ${t("sim.perMonth")}` : ""}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </motion.section>

          <motion.section
            data-testid="espace-dossier-card"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 rounded-3xl bg-white p-8 md:p-10"
          >
            <div className="flex items-center gap-3">
              <ClipboardCheck size={20} strokeWidth={1.5} className="text-sagedark" />
              <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">{t("espace.dossierTitle")}</h2>
            </div>
            <form data-testid="dossier-form" onSubmit={saveDossier} className="mt-8 grid md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="dossier-poste" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.poste")}</label>
                <input id="dossier-poste" data-testid="dossier-poste-input" className={`${field} mt-2`} placeholder={t("espace.postePh")} value={dossier.poste} onChange={(e) => setDossier({ ...dossier, poste: e.target.value })} />
              </div>
              <div>
                <label htmlFor="dossier-entreprise" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.entreprise")}</label>
                <input id="dossier-entreprise" data-testid="dossier-entreprise-input" className={`${field} mt-2`} placeholder={t("espace.entPh")} value={dossier.entreprise} onChange={(e) => setDossier({ ...dossier, entreprise: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="dossier-telephone" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.telephone")}</label>
                <input id="dossier-telephone" data-testid="dossier-telephone-input" type="tel" className={`${field} mt-2`} placeholder="+33 6 12 34 56 78" value={dossier.telephone} onChange={(e) => setDossier({ ...dossier, telephone: e.target.value })} />
                <p className="mt-2 text-xs text-smoke/70">{t("espace.telHint")}</p>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="dossier-objectifs" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.objectifs")}</label>
                <textarea id="dossier-objectifs" data-testid="dossier-objectifs-input" rows={3} className={`${field} mt-2 resize-none`} placeholder={t("espace.objPh")} value={dossier.objectifs} onChange={(e) => setDossier({ ...dossier, objectifs: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="dossier-notes" className="text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.notes")}</label>
                <textarea id="dossier-notes" data-testid="dossier-notes-input" rows={3} className={`${field} mt-2 resize-none`} placeholder={t("espace.notesPh")} value={dossier.notes} onChange={(e) => setDossier({ ...dossier, notes: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <button data-testid="dossier-save-button" type="submit" disabled={saving} className="rounded-full bg-ink text-paper px-8 py-3.5 text-sm hover:bg-sagedark disabled:opacity-50 transition-colors duration-300">
                  {saving ? t("espace.saving") : t("espace.save")}
                </button>
              </div>
            </form>
          </motion.section>

          <motion.section
            data-testid="espace-bilan-card"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-2 rounded-3xl bg-bone p-8 md:p-10"
          >
            <div className="flex items-center gap-3">
              <Sparkles size={20} strokeWidth={1.5} className="text-sagedark" />
              <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">{t("espace.bilan")}</h2>
            </div>
            {bilan === undefined ? (
              <p className="mt-5 text-sm text-smoke">{t("espace.loadingModule")}</p>
            ) : bilan ? (
              <>
                <div className="mt-6 space-y-3">
                  {Object.entries(bilan.scores).map(([k, v]) => (
                    <div key={k} data-testid={`bilan-score-${k}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="uppercase tracking-[0.15em] text-smoke">{dims[k] || k}</span>
                        <span className="font-serif text-ink">{v}</span>
                      </div>
                      <div className="h-1.5 bg-linen rounded-full overflow-hidden">
                        <motion.div className="h-full bg-sage rounded-full" initial={{ width: 0 }} animate={{ width: `${v}%` }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-5 text-xs text-smoke">{t("espace.dominant")} : <span className="text-sagedark font-medium">{dims[bilan.dominant]}</span> · {new Date(bilan.created_at).toLocaleDateString()}</p>
                <Link data-testid="bilan-retest-link" to={sub ? `/test?module=${sub.module.id}` : "/test"} className="mt-4 inline-flex text-sm text-ink underline underline-offset-4 hover:text-sagedark transition-colors duration-300">
                  {t("espace.retest")}{sub ? ` (${sub.module.name})` : ""}
                </Link>
              </>
            ) : (
              <>
                <p className="mt-5 text-sm text-smoke leading-relaxed">{t("espace.bilanEmpty")}</p>
                <Link data-testid="bilan-start-cta" to={sub ? `/test?module=${sub.module.id}` : "/test"} className="mt-6 inline-flex rounded-full bg-ink text-paper px-6 py-3 text-sm hover:bg-sagedark transition-colors duration-300">
                  {t("espace.bilanCta")} {sub ? sub.module.name : ""}
                </Link>
              </>
            )}
          </motion.section>

          <motion.section
            data-testid="espace-coaching-card"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-3 rounded-3xl bg-white p-8 md:p-10"
          >
            <div className="flex items-center gap-3">
              <Calendar size={20} strokeWidth={1.5} className="text-sagedark" />
              <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">{t("espace.coaching")}</h2>
            </div>
            <p className="mt-4 text-sm text-smoke leading-relaxed">{t("espace.coachingSub")}</p>

            {bookings.length > 0 && (
              <div className="mt-6 space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`booking-row-${b.id}`} className="flex items-center justify-between rounded-2xl bg-bone px-5 py-4">
                    <div>
                      <p className="text-sm text-ink capitalize">{formatDay(b.date)} · {b.time}</p>
                      <p className="text-xs text-smoke mt-1">{b.topic}</p>
                    </div>
                    <button data-testid={`booking-cancel-${b.id}`} onClick={() => cancelBooking(b.id)} className="p-2 text-smoke hover:text-red-700 transition-colors duration-300" aria-label="Annuler">
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form data-testid="booking-form" onSubmit={bookCoaching} className="mt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.step1")}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {slots.map((d) => (
                  <button
                    type="button"
                    key={d.date}
                    data-testid={`slot-day-${d.date}`}
                    onClick={() => { setSelDate(d.date); setSelTime(null); }}
                    disabled={d.times.length === 0}
                    className={`rounded-full px-5 py-2.5 text-sm capitalize transition-colors duration-300 ${
                      selDate === d.date ? "bg-ink text-paper" : "bg-bone text-ink hover:bg-linen"
                    } disabled:opacity-40`}
                  >
                    {formatDay(d.date)}
                  </button>
                ))}
              </div>
              {selDate && (
                <>
                  <p className="mt-6 text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.step2")}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {slots.find((d) => d.date === selDate)?.times.map((tm) => (
                      <button
                        type="button"
                        key={tm}
                        data-testid={`slot-time-${tm}`}
                        onClick={() => setSelTime(tm)}
                        className={`rounded-full px-5 py-2.5 text-sm transition-colors duration-300 ${
                          selTime === tm ? "bg-sagedark text-paper" : "bg-bone text-ink hover:bg-linen"
                        }`}
                      >
                        {tm}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-smoke">{t("espace.step3")}</p>
              <input
                data-testid="booking-topic-input"
                className={`${field} mt-3`}
                placeholder={t("espace.topicPh")}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                required
                minLength={3}
              />
              <button data-testid="booking-submit-button" type="submit" className="mt-6 rounded-full bg-ink text-paper px-8 py-3.5 text-sm hover:bg-sagedark transition-colors duration-300">
                {t("espace.book")}
              </button>
            </form>
          </motion.section>
        </div>
      </main>
    </div>
  );
}
